import type { DocNode } from '@atlaskit/adf-schema';
import type { SelectionAdfResult } from '@atlaskit/editor-plugin-selection-extension';

import { htmlToADF } from '@atlassian/html-adf-transformer';
import type { ContentType } from '@atlassian/ai-issue-create';

import { fg } from '@confluence/feature-gating';

import {
	constructInsertStorageFragmentRequestPayload,
	getValidRange,
	getValidRangeForActionItem,
} from '../services/issueInsertionUtils.tsx';
import type { IssueCreateSidePanelActions } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import { PageMode } from '../providers/IssueCreateSidePanelContextProvider.tsx';
import {
	ISSUE_CREATE_SIDE_PANEL_ID,
	ISSUE_CREATE_SIDE_PANEL_WIDTH,
} from '../ui/single-work-create/IssueCreateSidePanel.tsx';

import { includeEntireActionItemInSelection } from './actionItemUtils.tsx';
import {
	getSummaryAndDescriptionFromSelectionAdfResult,
	extractSelectedAdfSlice,
	normalizeToDocNode,
} from './selectionAdfUtils.ts';

type OptionalSelectionOrRange = Selection | Range | null | undefined;

export const SUMMARY_MAX_LENGTH = 254;

const getHTMLString = (activeSelection: OptionalSelectionOrRange): string => {
	if (!activeSelection || ('rangeCount' in activeSelection && activeSelection.rangeCount === 0)) {
		return '';
	}

	const range = 'getRangeAt' in activeSelection ? activeSelection.getRangeAt(0) : activeSelection;

	const docFragment = range.cloneContents();
	const tempDiv = document.createElement('div');
	tempDiv.appendChild(docFragment);

	return tempDiv.innerHTML;
};

export const extractSummary = (text: string, maxLength: number): [string, string] => {
	const horizontalEllipsis = '…';
	if (text.length <= maxLength) {
		return [text, ''];
	}
	const firstPartLength = maxLength - horizontalEllipsis.length;
	const firstPart = text.substring(0, firstPartLength);
	const firstPartWithHorizontalEllipsis = firstPart + horizontalEllipsis;
	return [firstPartWithHorizontalEllipsis, text];
};

export const getSummaryAndDescription = (
	activeSelection: OptionalSelectionOrRange,
): [string, DocNode] => {
	const selectionString = activeSelection?.toString() || '';
	const htmlString = getHTMLString(activeSelection);

	const [summary] = extractSummary(selectionString, SUMMARY_MAX_LENGTH);
	const summaryWithoutFormatting = summary.replace(/[\r\n\t]+/g, ' ');
	const descriptionAdf = htmlToADF(htmlString);

	return [summaryWithoutFormatting, descriptionAdf];
};

type ContentRef = HTMLElement | null | undefined;
type ConvertSelectionToLegacyFormat = (
	contentRef: ContentRef,
	range: Range | undefined,
	contentId: string,
) => {
	searchText: {
		numMatches: number;
		index: number;
		selectedText: string;
	};
};

type RightSidebarOpenContext = {
	open: (id: string, width: number) => void;
};

export interface OpenSingleIssueParams {
	actions: IssueCreateSidePanelActions;
	rightSidebarContext: RightSidebarOpenContext;
	activeSelection?: Selection | null;
	selectionAdfResult?: SelectionAdfResult;
	contentId: string;
	contentType?: ContentType;
	container: HTMLElement | null;
	lastModified?: string | null;
	pageMode?: PageMode;
	isActionItemInSelection?: boolean;
	convertSelectionToLegacyFormat: ConvertSelectionToLegacyFormat;
}

export const openSingleIssueSidePanelWithSelection = ({
	actions,
	rightSidebarContext,
	activeSelection,
	selectionAdfResult,
	contentId,
	contentType,
	container,
	lastModified,
	pageMode,
	isActionItemInSelection,
	convertSelectionToLegacyFormat,
}: OpenSingleIssueParams): void => {
	const { updatedSelection, activeSelectionRange } = expandSelectionIfActionItemsSelected({
		activeSelection,
		pageMode,
		isActionItemInSelection,
	});

	const shouldUseSelectionAdf = pageMode !== PageMode.VIEW && !!selectionAdfResult?.selectedNodeAdf;
	const selectionAdf = shouldUseSelectionAdf
		? extractSelectedAdfSlice(selectionAdfResult)
		: undefined;
	const highlightedText = shouldUseSelectionAdf
		? normalizeToDocNode(selectionAdf)
		: htmlToADF(getHTMLString(updatedSelection));

	const insertStorageFragmentRequestPayload = constructInsertStorageFragmentRequestPayload(
		contentId,
		convertSelectionToLegacyFormat,
		activeSelectionRange,
		container,
		lastModified,
	);

	const [summaryWithoutFormatting, descriptionAdf] = shouldUseSelectionAdf
		? getSummaryAndDescriptionFromSelectionAdfResult(selectionAdfResult, SUMMARY_MAX_LENGTH)
		: getSummaryAndDescription(updatedSelection);

	void actions.setSummary(summaryWithoutFormatting);
	void actions.setDescriptionAdf(descriptionAdf);
	void actions.setContentId(contentId);
	contentType && void actions.setContentType(contentType);
	void actions.setHighlightedText(highlightedText);
	void actions.setInsertStorageFragmentRequestPayload(insertStorageFragmentRequestPayload);
	void actions.setIsCreatedFromActionItem(!!isActionItemInSelection);
	void actions.setPageMode(pageMode);

	rightSidebarContext.open(ISSUE_CREATE_SIDE_PANEL_ID, ISSUE_CREATE_SIDE_PANEL_WIDTH);
};

const expandSelectionIfActionItemsSelected = ({
	activeSelection,
	pageMode,
	isActionItemInSelection,
}: {
	activeSelection?: Selection | null;
	pageMode?: PageMode;
	isActionItemInSelection?: boolean;
}): { updatedSelection: OptionalSelectionOrRange; activeSelectionRange: Range | undefined } => {
	let updatedSelection: OptionalSelectionOrRange = activeSelection;
	let activeSelectionRange = getValidRange(activeSelection?.getRangeAt(0));

	if (isActionItemInSelection && fg('confluence-create-work-items-from-action-items')) {
		const updatedActiveSelection = includeEntireActionItemInSelection(activeSelection, pageMode);
		if (updatedActiveSelection instanceof Range) {
			updatedSelection = updatedActiveSelection;
			activeSelectionRange = getValidRangeForActionItem(updatedActiveSelection);
		} else {
			activeSelectionRange =
				activeSelection && 'getRangeAt' in activeSelection
					? getValidRange(activeSelection.getRangeAt(0))
					: undefined;
		}
	}

	return { updatedSelection, activeSelectionRange };
};
